import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Search, 
  Calendar, 
  CreditCard, 
  Plane,
  ArrowRight,
  CheckCircle,
  Shield,
  HeadphonesIcon,
  Sparkles,
  Mail
} from 'lucide-react';
import { motion } from 'framer-motion';

const steps = [
  {
    number: "01",
    icon: Search,
    title: "Browse & Choose",
    description: "Explore our curated collection of group tours and solo adventures. Each trip is carefully designed to deliver unforgettable experiences.",
    details: [
      "Filter by destination, trip type, or dates",
      "Read detailed itineraries and what's included",
      "Check available tour dates and group sizes"
    ]
  },
  {
    number: "02",
    icon: Calendar,
    title: "Select Your Date",
    description: "Pick the tour date that works best for your schedule. Our trips run on set dates with small groups for a more personal experience.",
    details: [
      "Multiple departure dates available",
      "Small group sizes (typically 8-15 travelers)",
      "Solo travelers welcome on all trips"
    ]
  },
  {
    number: "03",
    icon: CreditCard,
    title: "Book & Pay",
    description: "Secure your spot with our flexible payment options. Pay in full or choose an installment plan that fits your budget.",
    details: [
      "No account required - guest checkout available",
      "Split payments into 3, 6, or 12 months",
      "Secure checkout powered by Stripe"
    ]
  },
  {
    number: "04",
    icon: Plane,
    title: "Pack & Go!",
    description: "We handle the logistics so you can focus on the adventure. Receive your travel documents and get ready for an amazing journey.",
    details: [
      "Detailed pre-trip information provided",
      "24/7 support during your trip",
      "Connect with fellow travelers before departure"
    ]
  }
];

const benefits = [
  {
    icon: Shield,
    title: "Secure Payments",
    description: "Your payment information is protected with industry-standard encryption."
  },
  {
    icon: HeadphonesIcon,
    title: "24/7 Support",
    description: "Our team is always available to help before, during, and after your trip."
  },
  {
    icon: Sparkles,
    title: "Curated Experiences",
    description: "Every itinerary is crafted by travel experts for authentic adventures."
  }
];

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-primary/5 to-transparent pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">
            How It Works
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Booking your dream adventure is simple. Here's everything you need to know about traveling with Circulate.
          </p>
        </div>
      </div>

      {/* Steps Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-12">
          {steps.map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
            >
              <Card className="border-0 shadow-lg overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    {/* Number & Icon */}
                    <div className="bg-secondary/10 p-8 flex flex-col items-center justify-center md:w-48">
                      <span className="text-5xl font-heading font-bold text-secondary/30 mb-2">
                        {step.number}
                      </span>
                      <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center">
                        <step.icon className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-8 flex-1">
                      <h3 className="font-heading text-2xl font-semibold text-primary mb-3">
                        {step.title}
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        {step.description}
                      </p>
                      <ul className="space-y-2">
                        {step.details.map((detail, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-muted-foreground">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Benefits Section */}
        <div className="mt-20">
          <h2 className="font-heading text-2xl font-semibold text-primary text-center mb-8">
            Why Book With Us
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <Card className="text-center h-full">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                      <benefit.icon className="h-7 w-7 text-secondary" />
                    </div>
                    <h3 className="font-heading font-semibold text-primary mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Payment Plans Section */}
        <div className="mt-20 bg-primary rounded-2xl p-8 md:p-12 text-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-4">
              Flexible Payment Plans
            </h2>
            <p className="text-white/80 mb-8">
              Don't let budget hold you back from your dream trip. Split your payment into manageable monthly installments with no hidden fees.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-3xl font-heading font-bold text-secondary">3</p>
                <p className="text-sm text-white/70">Monthly Payments</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-3xl font-heading font-bold text-secondary">6</p>
                <p className="text-sm text-white/70">Monthly Payments</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-3xl font-heading font-bold text-secondary">12</p>
                <p className="text-sm text-white/70">Monthly Payments</p>
              </div>
            </div>
            <p className="text-sm text-white/60">
              * Payment plans available at checkout. No interest or additional fees.
            </p>
          </div>
        </div>

        {/* Custom Trips Section */}
        <div className="mt-20">
          <Card className="border-2 border-secondary/20 bg-secondary/5">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 rounded-full bg-secondary/20 flex items-center justify-center">
                    <Sparkles className="h-12 w-12 text-secondary" />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-primary mb-3">
                    Want a Custom Experience?
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Don't see exactly what you're looking for? We specialize in creating custom itineraries 
                    tailored to your group's interests, dates, and budget. Whether it's a private group tour, 
                    a special celebration, or a unique destination — we've got you covered.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                    <Link to="/contact">
                      <Button className="bg-secondary hover:bg-secondary/90 text-white rounded-full px-6">
                        <HeadphonesIcon className="mr-2 h-4 w-4" />
                        Contact Us
                      </Button>
                    </Link>
                    <a href="mailto:info@circulatetravel.com">
                      <Button variant="outline" className="rounded-full px-6 border-secondary text-secondary hover:bg-secondary/10">
                        <Mail className="mr-2 h-4 w-4" />
                        info@circulatetravel.com
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <h2 className="font-heading text-2xl font-semibold text-primary mb-4">
            Ready to Start Your Adventure?
          </h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            Browse our collection of curated trips and find your next unforgettable experience.
          </p>
          <Link to="/trips">
            <Button className="bg-secondary hover:bg-secondary/90 text-white rounded-full px-8 py-6 text-lg">
              Explore Trips
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>

        {/* FAQ Link */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground">
            Still have questions?{' '}
            <Link to="/faq" className="text-secondary hover:underline">
              Check our FAQ
            </Link>
            {' '}or{' '}
            <Link to="/contact" className="text-secondary hover:underline">
              contact us
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
